/**
 * The tournament / arena / watch SIDE EFFECTS the reducers cannot own (M4 §1).
 *
 * A3a deliberately left seven things to the screen, because a pure reducer can
 * hold no timer, send no follow-up packet, raise no toast and start no REST call
 * (`PARITY.md`, "Still open here"). They all live here, in ONE store
 * subscription plus one 1 s tick, the same shape `useGameplayEffects` has:
 *
 *  1. the `pairing` timers — clear `roundStartAt` after 11 s
 *     (`actions.clearRoundStart`) and bump `gamesEnded` 15 s later (old
 *     index.js L1973-1976 `setTimeout(setGamesEnded + 1, 15000)`). The
 *     round-start STING is `rail/useSounds`' (one audio owner per lane);
 *  2. the `tournament_game_end_response` refetch throttle — one
 *     `actions.bumpGamesEnded()` per 10-15 s JITTERED window, never one per
 *     broadcast (game ends arrive ~1/s in a big round and a bump per event was
 *     an API storm across every client in the tournament);
 *  3. `getTournamentStatus` after a bye;
 *  4. `arenaStatus()` after an arena game ends and on every reconnect while an
 *     arena is open (old L3275-3282 / L3305-3310);
 *  5. the arena AUTO-RESUME send when `selectArenaAutoResumeDue` says a joined
 *     idle player is out of the pool (old L3286-3300). The once-per-visit gate
 *     is the core's (`arena.autoResumed`, raised by `actions.arenaResume`);
 *  6. the berserk refusal — a TOAST, which is this repo's expression of it (old
 *     L2265-2279; the app pops an `Alert`). Consumed with
 *     `actions.dismissBerserkRefusal()` so it can never repeat;
 *  7. the 1.5 s delay before the watch-end card → `useV2Ui.watchResultReady`.
 *
 * Fix G (2026-10-06) added an 8th, ordinary (dependency-driven, not imperative)
 * effect: picking the tournament's DEFAULT rail tab the moment one is entered
 * (G2) — 'details' when not joined yet, else the family's own tab
 * (`tabSpec.resultTabFor`), the SAME rule "Go to tournament" and the
 * "Tournament" action tile already use. It is CORE-ONLY (see the effect's own
 * comment for why — this hook may never call a `useQuery`); the family/
 * isJoined mirror that used to live here when this was first drafted moved to
 * `DetailsPanel` / `StandingsPanel`'s own models instead, which were already
 * calling `useTournamentDetails` safely inside the lazy tournament-panels
 * island (replacing what `pages/tournament/model.ts` used to do, reading
 * `ArenaStore`, written by the old `TournamentPlay`'s own REST fetch, before
 * that whole page was deleted).
 *
 * NO LANE GATE. The app's copy wraps every send in `isV2Active()` because RN
 * cannot reload a bundle, so its V2 reducers keep running after a hand-back to
 * the old screen. On the web a lane change always ends in a fresh document
 * (`PlayChessSwitch` reloads when it resolves 'old' with V2 handlers wired), so
 * there is no dormant-store case and `hooks/v2Active.ts` does not exist here.
 *
 * `showToast` is read from the toast CONTEXT — legitimately, because this hook
 * is the screen ROOT's (index.tsx), which is the one place allowed to read a
 * context (UX spec §6a). It is kept in a ref so the subscription registers once.
 */
import {useEffect, useRef} from 'react';

import {useToast} from '@/toast/useToast';
import {selectArenaAutoResumeDue} from '@/Store/liveGame/selectors';
import {liveGameStore, useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';

import {resultTabFor, selectRailTabInput, tabsFor} from '../rail/tabs/tabSpec';
import {useV2Ui} from '../theme/useV2Ui';

/** the cosmetic round countdown is dropped 11 s after a pairing. */
export const ROUND_START_CLEAR_MS = 11000;
/** old index.js L1973-1976: …and the standings are refetched 15 s after it. */
export const PAIRING_GAMES_ENDED_MS = 15000;
/**
 * Fix AD (owner 2026-10-07: "when the pairings-generated message comes the
 * refetch is not happening — generating_pairings and pairings_generated come
 * back to back"): the Pairings / Standings panels refetch this long after the
 * LAST of a round's start signals (`roundState` → 'pairings_generated', the
 * `pairing` broadcast's `pairingGenerated` flag, the round number itself).
 * The old `games.tsx` only refetches on the `gamesEnded` pulse — a `round`
 * prop change alone never does (its main effect is gated on a user action) —
 * and the status edge that bumps the round lands BEFORE the server has any
 * pairings for it. The 15 s bump above stays as the old screen's backstop.
 */
export const PAIRINGS_SETTLE_MS = 2000;
/** `10000 + random()*5000` — jittered so clients do not refetch in lockstep. */
export const GAMES_ENDED_MIN_MS = 10000;
export const GAMES_ENDED_JITTER_MS = 5000;
/** the watched game's end card waits 1.5 s. */
export const WATCH_END_DELAY_MS = 1500;
/** the cadence of the deadline checks that nothing in the store triggers */
const TICK_MS = 1000;

/**
 * old L2268-2272, verbatim: the server's refusal CODE mapped to copy. The core
 * records the raw code on `arena.berserkRefusal.reason`.
 */
export const BERSERK_REFUSAL_COPY: {[reason: string]: string} = {
  'window-closed': 'Berserk window closed',
  'clock-too-short': 'Clock too short to berserk',
};
export const BERSERK_REFUSAL_FALLBACK = 'Berserk unavailable right now';
/** old L2277: the toast's own dwell time. */
export const BERSERK_TOAST_MS = 4000;

/** old L1975 / the jitter, exported so the test can make it deterministic. */
export const gamesEndedWindowMs = (): number =>
  GAMES_ENDED_MIN_MS + Math.floor(Math.random() * GAMES_ENDED_JITTER_MS);

export function useTournamentEffects(): void {
  const showToast = useToast();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  useEffect(() => {
    const actions = () => liveGameStore.getState().actions;

    // ── 1. the pairing timers ────────────────────────────────────────────
    /** the `roundStartAt` stamp whose two timers are already armed */
    let armedRoundStartAt: number | null = null;
    let clearRoundTimer: any = null;
    let pairingBumpTimer: any = null;

    const onPairing = (state: LiveGameState) => {
      const at = state.tournament.roundStartAt;
      if (!at || at === armedRoundStartAt) {
        return;
      }
      armedRoundStartAt = at;
      clearTimeout(clearRoundTimer);
      clearTimeout(pairingBumpTimer);
      clearRoundTimer = setTimeout(() => {
        actions().clearRoundStart();
      }, ROUND_START_CLEAR_MS);
      pairingBumpTimer = setTimeout(() => {
        actions().bumpGamesEnded();
      }, PAIRING_GAMES_ENDED_MS);
    };

    // ── 1b. the new-round settle bump (fix AD) ───────────────────────────
    /** the three round-start signals this bump has already seen */
    let seenRoundForSettle: number | null = null;
    let seenRoundStateForSettle: string | null = null;
    let seenPairingGeneratedForSettle = false;
    let settleTimer: any = null;

    const onNewRoundSettle = (state: LiveGameState) => {
      const {currentRound, roundState, pairingGenerated} = state.tournament;
      const roundEdge = (currentRound ?? 0) > (seenRoundForSettle ?? 0);
      const stateEdge =
        roundState !== seenRoundStateForSettle &&
        roundState === 'pairings_generated';
      const flagEdge = pairingGenerated && !seenPairingGeneratedForSettle;
      seenRoundForSettle = currentRound;
      seenRoundStateForSettle = roundState;
      seenPairingGeneratedForSettle = pairingGenerated;
      // an arena has its own status/standings refetches (section 4) and its
      // `pairingGenerated` flag rises per arena pairing — Swiss only. No
      // `tournament.id` gate, same as `onPairing`: the broadcast can land
      // before the id is anchored, and a bump with no panel mounted is free.
      if (
        state.tournament.mode === 'arena' ||
        !(roundEdge || stateEdge || flagEdge)
      ) {
        return;
      }
      // back-to-back signals collapse into ONE bump, PAIRINGS_SETTLE_MS after
      // the last of them (a debounce, on purpose: the fetch must land once
      // the server has finished writing the round)
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        settleTimer = null;
        actions().bumpGamesEnded();
      }, PAIRINGS_SETTLE_MS);
    };

    // ── 2. the gamesEnded throttle ───────────────────────────────────────
    /** the `lastGameEndAt` stamp this window has already accounted for */
    let seenGameEndAt: number | null = null;
    let lastBumpAt = 0;
    let throttleTimer: any = null;

    const bump = () => {
      lastBumpAt = Date.now();
      throttleTimer = null;
      actions().bumpGamesEnded();
    };

    const onTournamentGameEnd = (state: LiveGameState) => {
      const at = state.tournament.lastGameEndAt;
      if (!at || at === seenGameEndAt) {
        return;
      }
      seenGameEndAt = at;
      const windowMs = gamesEndedWindowMs();
      const now = Date.now();
      if (now - lastBumpAt >= windowMs) {
        bump();
      } else if (!throttleTimer) {
        throttleTimer = setTimeout(bump, windowMs - (now - lastBumpAt));
      }
    };

    // ── 3. the bye's status refetch ──────────────────────────────────────
    /** the bye stamp already answered with a `get_tournament_status` */
    let seenByeAt: number | null = null;

    const onBye = (state: LiveGameState) => {
      const at = state.tournament.bye?.at ?? null;
      if (!at || at === seenByeAt) {
        return;
      }
      seenByeAt = at;
      actions().getTournamentStatus();
    };

    // ── 4. the arena status refetches ────────────────────────────────────
    /** the result key whose arena game end already asked for a fresh status */
    let seenArenaEndKey: string | null = null;
    let wasConnected = true;
    /**
     * Fix T / T3(a): the tableId a SPAWN has already asked a fresh status
     * for — Fix T §0 item 1: a player who joined before the start otherwise
     * plays their first game with `state === 'created'` still on the client.
     */
    let seenSpawnTableId: number | null = null;
    /** T3(b): whether `ended` has already asked for a fresh status this arena. */
    let seenEndedAsked = false;
    /**
     * T3(c): the tournament id ENTRY has already asked a status for, at most
     * once. Deliberately NOT seeded from the mount-time state (see `seed()`
     * below) — this edge exists FOR the reload/deep-link case, i.e. a mount
     * straight into an arena whose `arena.status` is already null, so it has
     * to be free to fire on this effect's very first `apply()` call.
     */
    let seenEntryTournamentId: string | null = null;

    const onArenaStatus = (state: LiveGameState) => {
      const arena = state.tournament.mode === 'arena' && !!state.tournament.id;
      if (!arena) {
        // leaving an arena re-arms every edge for the next one
        seenArenaEndKey = null;
        wasConnected = state.session.connected;
        seenSpawnTableId = state.game.tableId;
        seenEndedAsked = false;
        seenEntryTournamentId = null;
        return;
      }
      // …after an arena game ends
      const key = state.game.result?.key ?? null;
      if (key && key !== seenArenaEndKey) {
        seenArenaEndKey = key;
        actions().arenaStatus();
      }
      // T3(a): a NEW game SPAWNED.
      if (
        state.session.connected &&
        state.game.tableId !== null &&
        state.game.tableId !== seenSpawnTableId
      ) {
        seenSpawnTableId = state.game.tableId;
        actions().arenaStatus();
      }
      // T3(b): the tournament just ENDED — so the client sees the server's
      // `finished` status, not a stale running one.
      if (
        state.session.connected &&
        state.tournament.ended &&
        !seenEndedAsked
      ) {
        seenEndedAsked = true;
        actions().arenaStatus();
      }
      // T3(c): ENTRY — the family has just resolved to arena and no status
      // has ever landed for it (the reload / deep-link case), asked at most
      // once per tournament id.
      if (
        state.session.connected &&
        seenEntryTournamentId !== state.tournament.id &&
        state.arena.status === null
      ) {
        seenEntryTournamentId = state.tournament.id;
        actions().arenaStatus();
      }
      // …and on the rising edge of the socket coming back (old L3305-3310):
      // the server's state may have moved (a game ended, the arena finished)
      // while we were away.
      if (state.session.connected && !wasConnected) {
        actions().arenaStatus();
      }
      wasConnected = state.session.connected;
    };

    // ── 5. the auto-resume send ──────────────────────────────────────────
    const onAutoResume = (state: LiveGameState) => {
      // `arenaResume` raises `arena.autoResumed`, which is what makes the
      // selector false from here on — the old `arenaAutoResumedRef` (L339).
      if (selectArenaAutoResumeDue(state)) {
        actions().arenaResume();
      }
    };

    // ── 6. the berserk refusal toast ─────────────────────────────────────
    const onBerserkRefusal = (state: LiveGameState) => {
      const refusal = state.arena.berserkRefusal;
      if (!refusal) {
        return;
      }
      // consume it first: `dismissBerserkRefusal` writes the store, which
      // re-enters this subscription, and a second toast must not be queued.
      actions().dismissBerserkRefusal();
      const reason = String(refusal.reason);
      toastRef.current(
        BERSERK_REFUSAL_COPY[reason] || BERSERK_REFUSAL_FALLBACK,
        {
          duration: BERSERK_TOAST_MS,
        },
      );
    };

    // ── 7. the watch-end delay ───────────────────────────────────────────
    /** `<tableId>:<endType>` of the watch end whose card is already armed */
    let armedWatchEnd: string | null = null;
    let watchEndTimer: any = null;

    /** null while no watch has ended; a stable key once one has. */
    const watchEndKey = (state: LiveGameState): string | null =>
      state.watch.ended
        ? `${state.watch.tableId}:${state.watch.ended.endType}`
        : null;

    const onWatchEnd = (state: LiveGameState) => {
      const key = watchEndKey(state);
      if (!key) {
        if (armedWatchEnd !== null) {
          armedWatchEnd = null;
          clearTimeout(watchEndTimer);
          useV2Ui.getState().setWatchResultReady(false);
        }
        return;
      }
      if (armedWatchEnd === key) {
        return;
      }
      armedWatchEnd = key;
      clearTimeout(watchEndTimer);
      watchEndTimer = setTimeout(() => {
        useV2Ui.getState().setWatchResultReady(true);
      }, WATCH_END_DELAY_MS);
    };

    // ── S5. the rail follows the tournament ──────────────────────────────
    /** the roundState this effect has already acted on */
    let seenRoundStateForTab: string | null = null;
    /** `ended`'s own edge, tracked separately ("ended becomes true") */
    let seenEndedForTab = false;

    const onTournamentRailFollow = (state: LiveGameState) => {
      if (!state.tournament.id) {
        // the tournament surface is not up — never steer a rail that is not
        // showing this tournament at all.
        seenRoundStateForTab = state.tournament.roundState;
        seenEndedForTab = state.tournament.ended;
        return;
      }
      const {roundState, ended} = state.tournament;
      const tabs = tabsFor(selectRailTabInput(state));

      if (
        roundState !== seenRoundStateForTab &&
        (roundState === 'generating_pairings' ||
          roundState === 'pairings_generated') &&
        // Guard: do not switch to a tab `tabsFor` is not currently offering
        // (an arena's relabelled set still has the `pairings` KEY, so this
        // is safe in practice — assert it rather than assuming).
        tabs.some(t => t.key === 'pairings')
      ) {
        useV2Ui.getState().setRailTab('pairings');
      }
      // T5: family-aware — an arena's "home" tab is Leaderboard, not
      // Standings (`resultTabFor`, the SAME rule "Go to tournament" and the
      // idle Stage's "See leaderboard"/"See standings" already use).
      const homeTab = resultTabFor(state.tournament.mode === 'arena');
      if (ended && !seenEndedForTab && tabs.some(t => t.key === homeTab)) {
        useV2Ui.getState().setRailTab(homeTab);
      }
      seenRoundStateForTab = roundState;
      seenEndedForTab = ended;
    };

    // ── S5. re-send getTournamentStatus on (re)connect ───────────────────
    // NOT once at mount (that is what `seed()` below exists to prevent, the
    // same shape every other item here has) — `tournament_status_response`
    // is the ONLY authoritative source of 'in_progress' / 'completed' /
    // 'tournament_finished' after a reload or a socket drop, so a
    // reconnecting player must not sit on a stale status. The server is
    // idempotent, so firing it again costs nothing.
    /** the connected flag's own rising edge, independent of #4's arena-only one */
    let wasConnectedForStatus = true;
    /** the tournament id this effect has already asked a status for */
    let seenTournamentIdForStatus: string | null = null;

    const onReconnectStatus = (state: LiveGameState) => {
      const tournamentId = state.tournament.id;
      const connected = state.session.connected;
      if (tournamentId) {
        const idChanged = tournamentId !== seenTournamentIdForStatus;
        const reconnected = connected && !wasConnectedForStatus;
        if (idChanged || reconnected) {
          actions().getTournamentStatus();
        }
      }
      seenTournamentIdForStatus = tournamentId;
      wasConnectedForStatus = connected;
    };

    const apply = (state: LiveGameState) => {
      onPairing(state);
      onNewRoundSettle(state);
      onTournamentGameEnd(state);
      onBye(state);
      onArenaStatus(state);
      onAutoResume(state);
      onBerserkRefusal(state);
      onWatchEnd(state);
      onTournamentRailFollow(state);
      onReconnectStatus(state);
    };

    /**
     * Mount-time SEEDING, never application — the rule `useGameplayEffects`
     * documents. A same-document remount (the lobby page comes back after every
     * game) can find a bye, an arena result or a finished watch still on the
     * slices; replaying those would refetch, re-send and toast for events that
     * are over.
     *
     * The AUTO-RESUME is deliberately not seeded shut: being a joined idle
     * player in a running arena is a standing condition, not an edge, and
     * re-pooling on entry is exactly what the old effect does on mount
     * (L3286-3300). Its own once-per-visit gate is in the store.
     *
     * S5's two additions seed the SAME way as everything else here: a
     * mount onto an already-'pairings_generated' (or already-ended, or
     * already-open) tournament must not yank the tab or re-send a status
     * the moment this hook mounts — only a TRANSITION that happens while it
     * is alive does that.
     */
    const seed = (state: LiveGameState) => {
      armedRoundStartAt = state.tournament.roundStartAt;
      // fix AD: a mount onto a round already under way is not a round start
      seenRoundForSettle = state.tournament.currentRound;
      seenRoundStateForSettle = state.tournament.roundState;
      seenPairingGeneratedForSettle = state.tournament.pairingGenerated;
      seenGameEndAt = state.tournament.lastGameEndAt;
      seenByeAt = state.tournament.bye?.at ?? null;
      seenArenaEndKey = state.game.result?.key ?? null;
      wasConnected = state.session.connected;
      // T3(a)/(b): true EDGE detectors — seeded to the mount-time state so a
      // game already on the board, or an arena already ended, cannot look
      // like a fresh transition. T3(c) is deliberately left at its `null`
      // declaration value (see its own comment above) — it is not an edge,
      // it is the reload case, which this effect's first `apply()` call
      // below must still be free to act on.
      seenSpawnTableId = state.game.tableId;
      seenEndedAsked = state.tournament.ended;
      armedWatchEnd = watchEndKey(state);
      seenRoundStateForTab = state.tournament.roundState;
      seenEndedForTab = state.tournament.ended;
      seenTournamentIdForStatus = state.tournament.id;
      wasConnectedForStatus = state.session.connected;
      // a refusal recorded before this mount is CONSUMED, not voiced: the toast
      // belongs to the tap that was refused, not to a later mount.
      if (state.arena.berserkRefusal) {
        actions().dismissBerserkRefusal();
      }
    };

    seed(liveGameStore.getState());
    apply(liveGameStore.getState());

    const unsubscribe = liveGameStore.subscribe(apply);
    // The reconnect edge and the auto-resume window can both open while nothing
    // else in the store moves (the socket flag is mirrored by the root, but a
    // status response that makes the player 'idle' may land first).
    const interval = setInterval(() => {
      onAutoResume(liveGameStore.getState());
    }, TICK_MS);

    return () => {
      unsubscribe();
      clearInterval(interval);
      clearTimeout(clearRoundTimer);
      clearTimeout(pairingBumpTimer);
      clearTimeout(settleTimer);
      clearTimeout(throttleTimer);
      clearTimeout(watchEndTimer);
    };
  }, []);

  // ── 8. the default rail tab on ENTERING a tournament ────────────────────
  // CORE-ONLY on purpose: `useTournamentDetails` is a `useQuery`, which throws
  // with no `QueryClientProvider` above it — this hook runs for the screen's
  // WHOLE mount (every test of it, and of `index.tsx`, included), so it must
  // never call one unconditionally (the SAME rule `rail/live/LiveBody/model.ts`
  // documents for `HintCard`'s mutation). The family/isJoined mirror that used
  // to live here moved to `DetailsPanel` / `StandingsPanel`'s own models
  // instead — they ALREADY call `useTournamentDetails` safely, inside the lazy
  // tournament-panels island (`rail/tournament/__tests__/panels.test.ts`
  // already wraps its tests in a `QueryClientProvider` for exactly that call).
  //
  // `joined` here is therefore the cheaper, CORE-only signal (a live join this
  // session, or the arena mirror those two panels write) rather than the REST
  // `is_joined` — a fresh reload of an already-joined SWISS tournament can
  // still default to 'details' for one visit until a panel resolves it. That
  // is a one-tap inconvenience, not a correctness bug: `tabSpec.ts`'s
  // `activeTab` also falls back to the FIRST offered tab (not a hard-coded
  // one) if `railTab` is ever not actually on offer, so the rail is never
  // left showing the wrong body either way.
  const tournamentId = useLiveGame(s => s.tournament.id);
  const joinedCoreSignal = useLiveGame(
    s => s.tournament.join.code === 2 || s.arena.isJoined,
  );
  const isArenaCoreSignal = useLiveGame(s => s.tournament.mode === 'arena');
  const appliedDefaultTabFor = useRef<string | null>(null);
  useEffect(() => {
    if (!tournamentId) {
      appliedDefaultTabFor.current = null;
      return;
    }
    if (appliedDefaultTabFor.current === tournamentId) {
      return;
    }
    appliedDefaultTabFor.current = tournamentId;
    useV2Ui
      .getState()
      .setRailTab(
        joinedCoreSignal ? resultTabFor(isArenaCoreSignal) : 'details',
      );
  }, [tournamentId, joinedCoreSignal, isArenaCoreSignal]);
}

export default useTournamentEffects;
